/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { debounce } from 'lodash'
import { useMemo, useRef, useState } from 'react'

/**
 * Hook for carousel-style traversal through a list of items.
 * Provides prev/next traversal with wrap-around behavior.
 * Includes 300ms debounce to prevent query storms during rapid navigation.
 * Returns isNavigating state for subtle opacity transitions.
 *
 * @template T - Item type, must have an optional `id` property
 * @param items - Array of items to traverse through
 * @param currentItem - Currently selected item
 * @param onTraverse - Callback when traversing to a new item, receives (item, index)
 * @returns Object with next(), prev(), currentIndex, and isNavigating
 *
 * @example
 * const traversal = useListTraversal(
 *   contacts?.hits ?? [],
 *   focusedContact,
 *   (contact, index) => {
 *     setFocusedContact(contact)
 *   }
 * )
 *
 * <ContactDrawer
 *   nextContact={traversal.next}
 *   previousContact={traversal.prev}
 *   isNavigating={traversal.isNavigating}
 * />
 */
export function useListTraversal<T extends { id?: string }>(
  items: T[],
  currentItem: T | undefined,
  onTraverse: (item: T, index: number) => void,
) {
  const currentIndex = items.findIndex((item) => item.id === currentItem?.id)
  const itemsRef = useRef(items)
  const [isNavigating, setIsNavigating] = useState(false)
  itemsRef.current = items

  const debouncedTraverse = useMemo(
    () =>
      debounce((item: T, index: number) => {
        onTraverse(item, index)
        setIsNavigating(false)
      }, 300),
    [onTraverse],
  )

  const next = () => {
    if (itemsRef.current.length === 0) return

    setIsNavigating(true)
    // Carousel wrap-around: after last item, go to first
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % itemsRef.current.length
    debouncedTraverse(itemsRef.current[nextIndex], nextIndex)
  }

  const prev = () => {
    if (itemsRef.current.length === 0) return

    setIsNavigating(true)
    // Carousel wrap-around: before first item, go to last
    const prevIndex =
      currentIndex === -1
        ? itemsRef.current.length - 1
        : (currentIndex - 1 + itemsRef.current.length) % itemsRef.current.length
    debouncedTraverse(itemsRef.current[prevIndex], prevIndex)
  }

  return {
    next,
    prev,
    currentIndex,
    isNavigating,
  }
}
